import React from 'react';
import { Handle, Position, NodeProps, NodeResizer } from '@xyflow/react';
import { Box, Typography, Chip, Paper, useTheme } from '@mui/material';
import { GearIcon } from '@phosphor-icons/react';
import { StageData } from '../../../utils/redux/slices/callStagesFlowSlice';

const StageNode: React.FC<NodeProps> = ({ data, selected, id }) => {
  const theme = useTheme();
  const stageData = data as StageData;

  // Check if this is the initial stage
  const isInitialStage =
    id === 'start' || stageData?.stageName === 'Initial Stage';

  return (
    <>
      {/* Node Resizer - only show when selected */}
      <NodeResizer
        color={theme.palette.primary.main}
        isVisible={selected}
        minWidth={220}
        minHeight={100}
        maxWidth={500}
        maxHeight={300}
      />

      {/* Top Handle - only show if not initial stage */}
      {!isInitialStage && (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            width: 12,
            height: 12,
            backgroundColor: theme.palette.lightGray.main,
            border: `2px solid ${theme.palette.primary.main}`,
            top: -6,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        />
      )}

      {/* Bottom Handle - always show */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 12,
          height: 12,
          backgroundColor: theme.palette.lightGray.main,
          border: `2px solid ${theme.palette.primary.main}`,
          bottom: -6,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 6 : 2}
        sx={{
          minWidth: 220,
          minHeight: 100,
          maxWidth: 500,
          maxHeight: 300,
          width: '100%',
          height: '100%',
          border: selected
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.lightGray.main}`,
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                p: 1,
                backgroundColor: theme.palette.custom.sidebarIconBg,
                borderRadius: 1,
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <GearIcon size={16} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {stageData?.stageName}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.3,
                }}
              >
                {stageData?.systemPrompt}
              </Typography>
            </Box>
          </Box>

          {/* Chips container with flex-grow to push to bottom when resized */}
          <Box sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={
                  typeof stageData?.voice === 'string'
                    ? stageData.voice || 'default'
                    : stageData?.voice?.name || 'default'
                }
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  height: 24,
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.text.secondary,
                  borderColor: theme.palette.lightGray.main,
                }}
              />
              <Chip
                label={`temp: ${stageData?.temperature || 0.7}`}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  height: 24,
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.text.secondary,
                  borderColor: theme.palette.lightGray.main,
                }}
              />
              {stageData?.tools && stageData?.tools?.length > 0 && (
                <Chip
                  label={`${stageData?.tools?.length} tools`}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    backgroundColor: theme.palette.custom.sidebarIconBg,
                    color: theme.palette.primary.main,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default StageNode;
